/* eslint-disable react/jsx-handler-names */
import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { constants, labelStyle } from "../../../constants/pageConstants/pdpConstants";
import ButtonComponent from "../../atoms/button";
import Typography from "../../atoms/typography";
import { capitalizeFirstLetter } from "../../../utils/pdp/common";
import { useDispatch, useSelector } from "react-redux";
import { setClientCurrentClickedItem, setClientFlavorType } from "../../../store/slices/pdpSlice";
import Drawer from "../drawer";
import RadioItemList from "../radioItemList";
import { unlockBody } from "../../../utils/document";

/**
 * Component for displaying a list of flavor types.
 * @param {Array} props.flavorList - The list of flavor types to display.
 * @param {Array} props.isMobile - Is mobile check.
 * @returns {JSX.Element} The rendered `FlavorTypes` component.
 */
const FlavorTypes = ({ flavorList, isMobile }) => {
  const dispatch = useDispatch();
  const flavorDropDownRef = useRef(null);

  const [showFlavorList, setShowFlavorList] = useState(false);
  const clientVariantFlavour = useSelector((state) => state.pdp.clientVariantFlavour);
  const clientVariants = useSelector((state) => state.pdp.clientVariants);
  const clientVariantLabel = useSelector((state) => state.pdp.clientVariantLabel);
  const clientVariantType = useSelector((state) => state.pdp.clientVariantType);

  useEffect(() => {
    /**
     * Closes the dropdown if clicked outside.
     */
    const handleClickOutside = (event) => {
      if (flavorDropDownRef.current && !flavorDropDownRef.current.contains(event.target)) {
        setShowFlavorList(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  /**
   * Toggles the visibility of the flavor list.
   * @function onToggleFlavorList
   */
  const onToggleFlavorList = () => {
    setShowFlavorList(!showFlavorList);
  };

  /**
   * Handles the selection of a flavor and updates the state.
   * @function onFlavorSelection
   * @param {string} flavor - The selected flavor.
   */
  const onFlavorSelection = (flavor) => {
    unlockBody();
    dispatch(setClientFlavorType(flavor));

    const matchingVariant = clientVariants?.find((item) => item[clientVariantLabel]?.[clientVariantType]);

    if (matchingVariant) {
      const clickedItem = matchingVariant[clientVariantLabel]?.[clientVariantType]?.[flavor];
      dispatch(setClientCurrentClickedItem(clickedItem));
    }

    setShowFlavorList(false);
  };

  return (
    <>
      <Typography variant="p" Tag="p" classes={labelStyle}>
        {constants.SELECT_FLAVOR}
      </Typography>
      <div ref={flavorDropDownRef}>
        <ButtonComponent
          id="flavor-list-dropdown"
          subIcon={`/icons/${showFlavorList ? "arrow-up.svg" : "navigation-chevron.svg"}`}
          label={capitalizeFirstLetter(clientVariantFlavour)}
          size="customForPDP"
          buttonVariant="countryDropdown"
          onClick={onToggleFlavorList}
          classes="border-gray-300 h-auto"
        />
        {flavorList?.length > 0 &&
          (isMobile ? (
            <Drawer
              position="bottom"
              isOpen={showFlavorList}
              onDrawerClose={() => setShowFlavorList(false)}
              styles={"!p-0"}
            >
              <div className="border-b-2 px-16 py-12  text-base font-semibold">{constants.SELECT_FLAVOR}</div>
              {flavorList?.map((flavor, index) => {
                return (
                  <RadioItemList
                    id={flavor}
                    key={index}
                    isChecked={flavor === clientVariantFlavour}
                    onChange={() => onFlavorSelection(flavor)}
                    label={capitalizeFirstLetter(flavor)}
                  />
                );
              })}
            </Drawer>
          ) : (
            showFlavorList && (
              <div className="absolute z-[200] mt-4 flex w-[100%] flex-col overflow-auto rounded-8 border border-gray-300 bg-white-900 pl-2 pr-2  scrollbar-thin scrollbar-webkit">
                {flavorList?.map((flavor, index) => {
                  let newItem = { name: capitalizeFirstLetter(flavor) };
                  return (
                    <RadioItemList
                      radioListType="flavorType"
                      id={flavor}
                      key={index}
                      isChecked={flavor === clientVariantFlavour}
                      onClick={() => onFlavorSelection(flavor)}
                      listContent={newItem}
                      parentClass="border-b-1 p-2.5"
                    />
                  );
                })}
              </div>
            )
          ))}
      </div>
    </>
  );
};

export default FlavorTypes;

FlavorTypes.propTypes = {
  flavorList: PropTypes.array,
  isMobile: PropTypes.bool,
};
